import { Image } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

import { ImagePagination } from '@jane/bloom/components/imagePagination/imagePagination';
import type { imageSrcSet } from '@jane/bloom/viewModels/productImage';

import styles from './productImageCarousel.module.css';

interface ProductImageCarouselProps {
  className?: string;
  /** Letterbox images instead of cropping them, for stores that upload very tall compliance images. */
  containImages?: boolean;
  images: Array<NonNullable<ReturnType<typeof imageSrcSet>>>;
  overlayContent?: ReactNode;
  productName: string;
}

export const ProductImageCarousel = ({
  className,
  containImages = false,
  images,
  overlayContent,
  productName,
}: ProductImageCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const totalImages = images.length;
  const showCarousel = totalImages > 1;

  const nextIndex = (activeIndex + 1) % totalImages;
  const prevIndex = (activeIndex + totalImages - 1) % totalImages;

  // Handle swipe gestures
  const touchHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (showCarousel) {
        setActiveIndex(nextIndex);
      }
    },
    onSwipedRight: () => {
      if (showCarousel) {
        setActiveIndex(prevIndex);
      }
    },
    trackMouse: true,
  });

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!showCarousel) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setActiveIndex(prevIndex);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setActiveIndex(nextIndex);
      }
    },
    [showCarousel, prevIndex, nextIndex]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Update index function for pagination
  const updateIndex = (newIndex: number) => {
    if (newIndex >= 0 && newIndex < totalImages) {
      setActiveIndex(newIndex);
    }
  };

  // Don't render if no valid images
  if (totalImages === 0) {
    return null;
  }

  return (
    <div className={clsx(styles['carouselContainer'], className)}>
      <div
        className={styles['carouselWrapper']}
        {...(showCarousel ? touchHandlers : {})}
      >
        <div
          className={styles['carouselTrack']}
          style={{
            transform: `translateX(-${activeIndex * 100}%)`,
          }}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className={clsx(
                styles['imageItem'],
                containImages && styles['containImage']
              )}
            >
              <Image
                src={image.fallbackSrc}
                srcSet={image.srcSet}
                sizes={image.sizes}
                altText={`${productName} - Image ${index + 1}`}
                loading={index === 0 ? 'eager' : 'lazy'}
                objectFit="cover"
                /** 'contain' has nothing to letterbox within while the wrapper height is auto */
                height={containImages ? '100%' : undefined}
              />
            </div>
          ))}
        </div>

        {/* Overlay content (special badges, alerts, etc.) */}
        {overlayContent && (
          <div className={styles['overlayContent']}>{overlayContent}</div>
        )}

        {/* Pagination controls - only show for multiple images */}
        {showCarousel && (
          <ImagePagination
            activeIndex={activeIndex}
            overridePosition="bottom"
            totalItems={totalImages}
            updateIndex={updateIndex}
          />
        )}
      </div>
    </div>
  );
};
